import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { InputNumberMask } from '../../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Text } from '../../../../../../../../../CommonComponents/OPL/Text/Text';
import { Wrapper } from '../../../../../../../../../CommonComponents/OPL/Wrapper/Wrapper';
import { DEFAULT_NET_PROCEEDS, FormValues } from '../../BusinessSale.meta';
import { StyledHtmlTable } from './NetProceedsFromSale.styles';

interface NetProceedsFromSaleProps {
  index: number;
  disabled?: boolean;
}

export const NetProceedsFromSale = ({ index, disabled = false }: NetProceedsFromSaleProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  const { fields, append: appendProceeds, remove: removeProceeds } = useFieldArray({
    control,
    name: `Sales.${index}.NetProceedsFromSale`,
  });

  return (
    <Wrapper>
      <Box display="flex" alignItems="center" justifyContent="space-between" my="16">
        <Text fontWeight={600}>Net Proceeds from Sale</Text>
        <Button
          variant="link"
          leftIcon="fas fa-plus"
          onClick={() =>
            appendProceeds({ YearsAfterSaleYear: fields.length + 1, ...DEFAULT_NET_PROCEEDS })
          }
          disabled={disabled}
        >
          Add Year
        </Button>
      </Box>

      <StyledHtmlTable variant="embedded" columnSpacing="small">
        <table>
          <colgroup>
            <col css="width: 15%;" />
            <col css="width: 35%;" />
            <col css="width: 35%;" />
            <col css="width: 15%;" />
          </colgroup>
          <thead>
            <tr>
              <th css="text-align: center;">
                <Text variant="smallCaps">Year</Text>
              </th>
              <th css="text-align: left;">
                <Text variant="smallCaps">Amount</Text>
              </th>
              <th css="text-align: left;">
                <Text variant="smallCaps">Net Worth</Text>
              </th>
              <th css="text-align: center;">
                <Text variant="smallCaps">&nbsp;</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map(({ id }, i) => (
              <tr key={id}>
                <td css="text-align: center;">
                  <Text>{i + 1}</Text>
                </td>
                <td css="text-align: left;">
                  <Controller
                    control={control}
                    name={`Sales.${index}.NetProceedsFromSale.${i}.SaleAmount`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        aria-label="Sale Amount"
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        leftIcon="far fa-dollar-sign"
                        formVariant={
                          errors.Sales?.[index]?.NetProceedsFromSale?.[i]?.SaleAmount && 'error'
                        }
                        helperText={
                          errors.Sales?.[index]?.NetProceedsFromSale?.[i]?.SaleAmount?.message
                        }
                        onValueChange={({ value }) => {
                          onChange(value);
                        }}
                        disabled={disabled}
                        {...field}
                      />
                    )}
                  />
                </td>
                <td css="text-align: left;">
                  <Controller
                    control={control}
                    name={`Sales.${index}.NetProceedsFromSale.${i}.NetWorthPercent`}
                    render={({ field: { onChange, ...field } }) => (
                      <InputNumberMask
                        aria-label="NetWorth Percentage"
                        allowNegative={false}
                        rightIcon="far fa-percent"
                        formVariant={
                          errors.Sales?.[index]?.NetProceedsFromSale?.[i]?.NetWorthPercent &&
                          'error'
                        }
                        helperText={
                          errors.Sales?.[index]?.NetProceedsFromSale?.[i]?.NetWorthPercent?.message
                        }
                        onValueChange={({ value }) => {
                          onChange(value);
                        }}
                        disabled={disabled}
                        {...field}
                      />
                    )}
                  />
                </td>
                <td css="text-align: center;">
                  {fields.length > 1 && (
                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      aria-label="Delete proceed"
                      onClick={() => removeProceeds(i)}
                      disabled={disabled}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </StyledHtmlTable>
    </Wrapper>
  );
};
